import { redirect } from 'react-router-dom';

import getCSRFToken from '../general-utilities/getCsrfToken';
import {
  DroneError,
  DroneException,
  ErrorDetails,
  ErrorResponse,
  ConflictException,
  ForbiddenException,
  InvalidSessionException,
  ServerException,
  ValidationException,
  NotFoundException
} from './Exceptions';

function isErrorDetails(value: unknown): value is ErrorDetails {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;
  return Object.values(record).every((v) => Array.isArray(v) && v.every((x) => typeof x === 'string'));
}

function normalizeErrorResponse(responseStatus: number, payload: unknown): ErrorResponse {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const status = typeof record.status === 'number' ? record.status : responseStatus;
    const message =
      typeof record.message === 'string'
        ? record.message
        : typeof record.detail === 'string'
          ? record.detail
          : 'Request failed';

    const details = isErrorDetails(record.details) ? record.details : undefined;
    return { status, message, details };
  }

  return { status: responseStatus, message: 'Request failed' };
}

export async function makeRequest<T>(
  baseURL: string,
  url: string,
  options: RequestInit,
  authRequired: boolean,
  leaveOutCsrfToken?: boolean
): Promise<T> {
  const csrfToken = leaveOutCsrfToken ? null : getCSRFToken();

  const fetchOptions: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      ...(options.headers || {})
    }
  };

  if (authRequired) {
    fetchOptions.credentials = 'include';
  }

  try {
    const response = await fetch(`${baseURL}${url}`, fetchOptions);
    if (!response.ok) {
      const responseData = await response.json();
      // Handle nested error structure: { error: { message, status } }
      const errorPayload: unknown = (responseData as { error?: unknown }).error ?? responseData;
      const errorResponse: ErrorResponse = normalizeErrorResponse(response.status, errorPayload);

      switch (response.status) {
        case 401:
          throw InvalidSessionException.fromErrorResponse(errorResponse);
        case 403:
          // TODO: discuss how to handle forbidden; maybe redirect to forbidden page.
          throw ForbiddenException.fromErrorResponse(errorResponse);
        case 409:
          throw ConflictException.fromErrorResponse(errorResponse);
        case 422:
        case 400:
          // validation error
          throw ValidationException.fromErrorResponse(errorResponse);
        case 404:
          throw NotFoundException.fromErrorResponse(errorResponse);
        case 500:
        case 408:
          throw ServerException.fromErrorResponse(errorResponse);
        case 424:
          throw DroneException.fromErrorResponse(errorResponse as DroneError);
        default:
          throw new Error('Unexpected error');
      }
    }
    if (response.status === 204) {
      return { status: 204 } as T;
    }

    return await response.json();
  } catch (error: any) {
    if (error instanceof InvalidSessionException) {
      redirect('/');
    }
    if (error instanceof ForbiddenException) {
      redirect('/forbidden');
    }
    throw error;
  }
}
